import * as React from "react";
import { connect } from "react-redux";
import { Form, Formik } from "formik";
import { RouteComponentProps } from "react-router";

import * as Yup from "yup";
import classnames from "classnames";

import { Input, Button, FormGroup } from "reactstrap";

import { RootState } from "../../store/rootReducer";
import { Accident } from "../../lib/interfaces/Accident.interface";
import { Location, Position } from "../../lib/interfaces/Location.interface";

import CustomSelect from "../../common/CustomSelect.component";
import DatePickerCard from "../../common/DatePickerCard.component";
import LocationSelect from "../../common/LocationSelect.component";
import Breadcrumbs, { BreadcrumbType } from "../Breadcrumbs.component";

import * as appSelectors from "../../store/app/selectors";
import * as accidentsActions from "../../store/accidents/actions";

const get = require("lodash/get");

const moment = require("moment");

interface RouteInfo {
  accidentId?: string;
}

export interface AccidentCreationPageProps
  extends RouteComponentProps<RouteInfo> {
  accident: Accident;
  fetchAccident: (accidentId: string) => void;
  upsertAccident: (accidentId: Accident) => void;
  getLocationWithId: (locationId: number) => Location | undefined;
}

interface AccidentCreationPageState {
  pageStatus: "loading" | "done";
}

class AccidentCreationPage extends React.PureComponent<
  AccidentCreationPageProps,
  AccidentCreationPageState
> {
  constructor(props) {
    super(props);

    this.onSubmit = this.onSubmit.bind(this);

    this.state = {
      pageStatus: this.accidentId ? "loading" : "done",
    };

    if (this.accidentId) {
      const { fetchAccident } = this.props;
      fetchAccident(this.accidentId);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { accident } = this.props;

    if (this.accidentId !== get(prevProps, "match.params.accidentId")) {
      const { fetchAccident } = this.props;
      this.setState({ pageStatus: "loading" }, () =>
        fetchAccident(this.accidentId)
      );
    } else if (accident && !prevProps.accident) {
      this.setState({ pageStatus: "done" });
    }
  }

  get accidentId() {
    return get(this.props.match, "params.accidentId");
  }

  get validationSchema() {
    return Yup.object().shape({
      lossDate: Yup.date().required(),
      locationId: Yup.number().required(),
      locationPositionId: Yup.number().required(),
    });
  }

  get initialValues() {
    const { accident } = this.props;
    const { pageStatus } = this.state;

    if (this.accidentId && accident && pageStatus === "done") {
      return {
        id: accident.id,
        locationPositionId: get(accident, "locationPosition.id"),
        locationId: get(accident, "locationPosition.location.id"),
        lossDate: accident.lossDate && new Date(accident.lossDate),
      };
    }

    return null;
  }

  onSubmit(values) {
    const { upsertAccident } = this.props;
    console.log("values", values);
    upsertAccident(values as Accident);
  }

  renderForm() {
    const { getLocationWithId } = this.props;

    return (
      <Formik
        enableReinitialize
        initialValues={this.initialValues}
        onSubmit={this.onSubmit}
        validationSchema={this.validationSchema}
      >
        {(formProps) => {
          let currentPositions: Position[] | undefined;
          if (formProps.values.locationId) {
            const location = getLocationWithId(formProps.values.locationId);

            if (location) {
              currentPositions = location.locationPositions;
            }
          }

          let lossDateWarning;
          if (formProps.values.lossDate) {
            const lossDate = moment(formProps.values.lossDate);
            const lossDateDifference = moment().endOf("day").diff(lossDate);
            lossDateWarning =
              lossDateDifference >= 0 ? undefined : "Date is in the future";
          }

          return (
            <Form>
              <div className="row no-gutters mt-3">
                <div className="col-12 mb-3">
                  <div className="row no-gutters">
                    <div className="col-12">
                      <DatePickerCard
                        title="Loss date"
                        type="date-time"
                        error={lossDateWarning}
                        value={formProps.values.lossDate}
                        cardClassName={classnames({
                          "ggcard--error": !!formProps.errors.lossDate,
                        })}
                        onChange={(value) =>
                          formProps.setFieldValue(
                            "lossDate",
                            value ? value.toISOString() : undefined
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-6 col-lg-4 pr-md-3">
                  <div
                    style={{ overflow: "visible" }}
                    className={classnames("ggcard p-2", {
                      "ggcard--error": !!formProps.errors.locationId,
                    })}
                  >
                    <LocationSelect
                      type="normal"
                      hideAllLocationsOption
                      style={{ width: "100%" }}
                      value={formProps.values.locationId}
                      placeholder="Select an accident location"
                      valueContainerStyle={{ textAlign: "left" }}
                      placeholderStyle={{
                        fontWeight: "light",
                        fontStyle: "italic",
                      }}
                      getLocationLabel={(location) =>
                        `${location.code} - ${location.name}`
                      }
                      onChange={(value) => {
                        formProps.setFieldValue("locationId", value);
                        formProps.setFieldValue("locationPositionId", null);
                      }}
                    />
                  </div>
                </div>

                {formProps.values.locationId && (
                  <div className="col-12 col-md-6 col-lg-4 pr-lg-3 mt-3 mt-md-0">
                    <div
                      style={{ overflow: "visible" }}
                      className={classnames("ggcard p-2", {
                        "ggcard--error": !!formProps.errors.locationPositionId,
                      })}
                    >
                      <CustomSelect
                        type="normal"
                        style={{ width: "100%" }}
                        options={currentPositions}
                        getOptionValue={(position) => position.id}
                        placeholder="Select an accident position"
                        getOptionLabel={(position) => position.name}
                        value={formProps.values.locationPositionId}
                        valueContainerStyle={{ textAlign: "left" }}
                        placeholderStyle={{
                          fontWeight: "light",
                          fontStyle: "italic",
                        }}
                        onChange={(value) =>
                          formProps.setFieldValue("locationPositionId", value)
                        }
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="row no-gutters mt-5">
                <div className="col-12">
                  <Button
                    color="primary"
                    className="w-100"
                    disabled={!formProps.isValid}
                  >
                    Submit accident
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    );
  }

  render() {
    const { pageStatus } = this.state;

    const breadcrumbs: [BreadcrumbType] = [
      { name: "Accidents", link: "/accidents" },
    ];

    if (this.accidentId) {
      breadcrumbs.push({
        name: `ERAC ${this.accidentId}`,
        link: `/accidents/${this.accidentId}`,
      });
      breadcrumbs.push({ name: "Edit", active: true });
    } else {
      breadcrumbs.push({ name: "Report new accident", active: true });
    }

    return (
      <div>
        <Breadcrumbs breadcrumbs={breadcrumbs} />

        {pageStatus === "loading" ? (
          <div className="p-3">Loading...</div>
        ) : (
          <div className="page accidentreport-page container-fluid">
            <section>
              <h5 className="title">Report New Accident</h5>
              {this.renderForm()}
            </section>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  accident: state.accidents.accidentDetails,
  getLocationWithId: appSelectors.getLocationWithId(state),
});

const mapDispatchToProps = (dispatch) => ({
  fetchAccident: (accidentId: string) =>
    dispatch(accidentsActions.fetchAccident(accidentId)),
  upsertAccident: (accident: Accident) =>
    dispatch(accidentsActions.upsertAccident(accident)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AccidentCreationPage);



// WEBPACK FOOTER //
// ./src/routes/accidents/AccidentCreation.page.tsx